import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { bookingService } from '@/lib/api/booking.service';
import { handleApiError } from '@/lib/api/client';
import type { Booking, BookingStatus } from '@/types/booking.types';

const STATUS_BADGE: Record<BookingStatus, { label: string; className: string }> = {
  pending:     { label: 'Pending',     className: 'bg-amber-100 text-amber-800' },
  confirmed:   { label: 'Confirmed',   className: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'In Progress', className: 'bg-indigo-100 text-indigo-800' },
  completed:   { label: 'Completed',   className: 'bg-green-100 text-green-800' },
  cancelled:   { label: 'Cancelled',   className: 'bg-red-100 text-red-800' },
  disputed:    { label: 'Disputed',    className: 'bg-orange-100 text-orange-800' },
};

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString();

const getAwarded = (booking: Booking): boolean => {
  const award = booking.metadata?.award;
  return typeof award === 'object' && award !== null && (award as { awarded?: unknown }).awarded === true;
};

export const BookingManagementPage: React.FC = () => {
  const { t } = useTranslation(['bookings', 'common']);
  const { celebrationId } = useParams<{ celebrationId: string }>();
  const navigate = useNavigate();

  const celebrationIdNum = useMemo(() => Number.parseInt(celebrationId ?? '', 10), [celebrationId]);

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState<number | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<number | null>(null);
  const [awardNoteByBookingId, setAwardNoteByBookingId] = useState<Record<number, string>>({});
  const [signingReferenceByBookingId, setSigningReferenceByBookingId] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    if (!Number.isFinite(celebrationIdNum)) return;
    setLoading(true);
    setError(null);
    try {
      const data = await bookingService.listBookings(celebrationIdNum);
      setBookings(data);
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [celebrationIdNum]);

  useEffect(() => {
    load();
  }, [load]);

  const handleTransition = async (bookingId: number, status: string) => {
    setTransitioning(bookingId);
    setError(null);
    try {
      const updated = await bookingService.transitionStatus(celebrationIdNum, bookingId, status);
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? updated : b)));
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setTransitioning(null);
      setConfirmCancelId(null);
    }
  };

  return (
    <>
      <AppBreadcrumbs />
      <PageContainer width="wide">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{t('bookings:title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && (
              <div className="flex items-center justify-center py-8">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            )}

            {error && (
              <div className="rounded-md bg-destructive/10 text-destructive px-4 py-2 text-sm mb-4">
                {error}
              </div>
            )}

            {!loading && !error && bookings.length === 0 && (
              <p className="text-muted-foreground text-sm py-4">No bookings found for this celebration.</p>
            )}

            {!loading && bookings.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-3 font-medium">Booking #</th>
                      <th className="pb-2 pr-3 font-medium">Vendor ID</th>
                      <th className="pb-2 pr-3 font-medium">Event Date</th>
                      <th className="pb-2 pr-3 font-medium">Status</th>
                      <th className="pb-2 pr-3 font-medium">Award</th>
                      <th className="pb-2 pr-3 font-medium">Contract</th>
                      <th className="pb-2 pr-3 font-medium">Total</th>
                      <th className="pb-2 pr-3 font-medium">Payment</th>
                      <th className="pb-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => {
                      const badge = STATUS_BADGE[booking.status] ?? { label: booking.status, className: 'bg-gray-100 text-gray-800' };
                      const isBusy = transitioning === booking.id;
                      const isAwarded = getAwarded(booking);
                      const isSigned = booking.contract_signed;
                      return (
                        <tr key={booking.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/celebrations/${celebrationIdNum}/bookings/${booking.id}`)}>
                          <td className="py-2 pr-3 font-mono text-primary hover:underline">{booking.booking_number}</td>
                          <td className="py-2 pr-3">{booking.vendor_id}</td>
                          <td className="py-2 pr-3">
                            {booking.event_date ? formatDate(booking.event_date) : '—'}
                          </td>
                          <td className="py-2 pr-3">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="py-2 pr-3">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${isAwarded ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                              {isAwarded ? 'Awarded' : 'Not Awarded'}
                            </span>
                          </td>
                          <td className="py-2 pr-3">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${isSigned ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                              {isSigned ? 'Signed' : 'Unsigned'}
                            </span>
                          </td>
                          <td className="py-2 pr-3">
                            {formatCurrency(booking.total_amount, booking.currency)}
                          </td>
                          <td className="py-2 pr-3 capitalize">{booking.payment_status}</td>
                          <td className="py-2" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  navigate(`/celebrations/${celebrationIdNum}/bookings/${booking.id}/line-items`)
                                }
                              >
                                View Line Items
                              </Button>

                              {booking.status === 'pending' && (
                                <>
                                  {!isAwarded && (
                                    <div className="flex items-center gap-2">
                                      <input
                                        className="h-8 rounded-md border border-input bg-background px-2 text-xs w-44"
                                        value={awardNoteByBookingId[booking.id] ?? ''}
                                        onChange={(e) => setAwardNoteByBookingId((prev) => ({
                                          ...prev,
                                          [booking.id]: e.target.value,
                                        }))}
                                        placeholder="Award note"
                                      />
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={async () => {
                                          setTransitioning(booking.id);
                                          setError(null);
                                          try {
                                            const updated = await bookingService.awardBooking(
                                              celebrationIdNum,
                                              booking.id,
                                              { award_note: awardNoteByBookingId[booking.id] || undefined },
                                            );
                                            setBookings((prev) => prev.map((b) => (b.id === booking.id ? updated : b)));
                                          } catch (err: unknown) {
                                            setError(handleApiError(err));
                                          } finally {
                                            setTransitioning(null);
                                          }
                                        }}
                                        disabled={isBusy}
                                      >
                                        {isBusy ? 'Awarding…' : 'Award Vendor'}
                                      </Button>
                                    </div>
                                  )}

                                  {isAwarded && !isSigned && (
                                    <div className="flex items-center gap-2">
                                      <input
                                        className="h-8 rounded-md border border-input bg-background px-2 text-xs w-44"
                                        value={signingReferenceByBookingId[booking.id] ?? ''}
                                        onChange={(e) => setSigningReferenceByBookingId((prev) => ({
                                          ...prev,
                                          [booking.id]: e.target.value,
                                        }))}
                                        placeholder="Signing reference"
                                      />
                                      <Button
                                        size="sm"
                                        onClick={async () => {
                                          setTransitioning(booking.id);
                                          setError(null);
                                          try {
                                            const updated = await bookingService.signBooking(
                                              celebrationIdNum,
                                              booking.id,
                                              { signing_reference: signingReferenceByBookingId[booking.id] || undefined },
                                            );
                                            setBookings((prev) => prev.map((b) => (b.id === booking.id ? updated : b)));
                                          } catch (err: unknown) {
                                            setError(handleApiError(err));
                                          } finally {
                                            setTransitioning(null);
                                          }
                                        }}
                                        disabled={isBusy}
                                      >
                                        {isBusy ? 'Signing…' : 'Mark Signed'}
                                      </Button>
                                    </div>
                                  )}

                                  {isAwarded && isSigned && (
                                    <Button
                                      size="sm"
                                      onClick={() => handleTransition(booking.id, 'confirmed')}
                                      disabled={isBusy}
                                    >
                                      {isBusy ? 'Confirming…' : 'Confirm'}
                                    </Button>
                                  )}
                                </>
                              )}

                              {booking.status === 'confirmed' && (
                                <>
                                  {confirmCancelId === booking.id ? (
                                    <>
                                      <span className="text-xs text-muted-foreground">Sure?</span>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => handleTransition(booking.id, 'cancelled')}
                                        disabled={isBusy}
                                      >
                                        {isBusy ? 'Cancelling…' : 'Yes, Cancel'}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setConfirmCancelId(null)}
                                      >
                                        No
                                      </Button>
                                    </>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setConfirmCancelId(booking.id)}
                                    >
                                      Cancel
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </PageContainer>
    </>
  );
};
